/* eslint-disable arrow-body-style */
import { getDynamicLocalCnEnText, getWeekDay } from "@/pages/dashboard/utils/utils";
import { getLocale } from "umi";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { metricRender } from "@/pages/dashboard/pages/comparison/_config/hooks";
import { useLocalText, useWeekDayArr } from "@/pages/dashboard/utils/customHook";
import { getMonetUnit } from "@/pages/dashboard/utils/moneyUnit";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { appannieTypeSourceList, CASUAL_METRIC_EXPLAIN, GAME_HIDDEN_METRICS_MAP } from "./constants";
import enLocale from '@/pages/dashboard/_locales/enCN/competitor';
import zhLocale from '@/pages/dashboard/_locales/zhCN/competitor';
import enCommon from '@/pages/dashboard/_locales/enCN/common';
import zhCommon from '@/pages/dashboard/_locales/zhCN/common';

const widthLarge = 220;
const widthMid = 240;
const nameMap = {
  Day: 'dau',
  Week: 'wau',
  Month: 'mau',
};

const localTextConfig = () => {
  return [
    { id: "common_date", key: "date" },
    { id: "competitor_launch", key: "launchDate" },
    { id: "competitor_earliest_data", key: "earliestData" },
    { id: "competitor_key_metrics_performance_game", key: "game" },
    { id: "competitor_key_metrics_performance_revenue", key: "revenue", values: { unit: getMonetUnit() } },
    { id: "competitor_key_metrics_performance_adjusted_revenue", key: "adjusted_revenue", values: { unit: getMonetUnit() } },
    { id: "competitor_key_metrics_performance_dau", key: "dau" },
    { id: "competitor_key_metrics_performance_avg_dau", key: "avgDau" },
    { id: "competitor_key_metrics_performance_wau", key: "wau" },
    { id: "competitor_key_metrics_performance_mau", key: "mau" },
    { id: "competitor_key_metrics_performance_new", key: "new" },
    { id: "competitor_key_metrics_performance_d2", key: "d2" },
    { id: "competitor_key_metrics_performance_d3", key: "d3" },
    { id: "competitor_key_metrics_performance_d7", key: "d7" },
    { id: "competitor_key_metrics_performance_d15", key: "d15" },
    { id: "competitor_key_metrics_performance_d31", key: "d31" },
    { id: "competitor_key_metrics_performance_tru", key: "tru" },
    { id: "competitor_key_metrics_performance_total_revenue", key: "total_revenue", values: { unit: getMonetUnit() } },
    {
      id: "common_Region",
      key: "region",
    },
    {
      id: "common_Country",
      key: "country",
    },
    {
      id: "common_System",
      key: "system",
    },
  ];
};

export const RANGE_DATE_KEY_MAP = {
  date: 'Day',
  week: 'Week',
  month: 'Month'
} as any;

export default function useTableColumns(gameCode, explainInfo, filterValue, accumulates, showType,competitorReqType, isCasual = false) {
  const localText = useLocalText(localTextConfig());
  const [columns, setColumns] = useState([]);
  let { source, timeType, switchDatePicker, dayMonth } = filterValue || {};
  const { isEarliestData } = filterValue || {};
  // 新筛选器转换
  if (filterValue?.newFilter) {
    const { filterValue: newFilterValue, isLaunchTime } = filterValue;
    source = newFilterValue?.source;
    switchDatePicker = isLaunchTime || isEarliestData;
    timeType = RANGE_DATE_KEY_MAP[newFilterValue?.rangeTime?.type];
    dayMonth = newFilterValue?.rangeDayMonth;
  }
  const localLanguage = getLocale();
  const weekDayArr = useWeekDayArr();


  // casual 模式只展示 MAU、New、TNU
  const CASUAL_METRICS = ['dau', 'new', 'tru'];

  const isAlignedBy = !!switchDatePicker;
  const hiddenMetricsConfig = GAME_HIDDEN_METRICS_MAP[gameCode];
  const hiddenMetrics = isAlignedBy
    ? (hiddenMetricsConfig?.alignedBy || [])
    : (hiddenMetricsConfig?.normal || []);

  const COLUMNS_CONFIG = useMemo(()=>{
    const allColumns = [
      { value: 'revenue', text: 'Revenue($)', width: widthLarge + 30, format: '0.00', isPercent: false, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Revenue($)' },
      { value: 'adjusted_revenue', text: 'Adjusted Revenue($)', width: widthLarge + 30, onlyValueWidth: widthLarge + 5, format: '0.00', isPercent: false, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Adjusted Revenue($)' },
      { value: 'dau', text: "nameMap[type[2]]", width: widthLarge + 30, format: '0', isPercent: false, decimals: 0, isUserType: true, dodDecimals: 1, rainbowKey: nameMap?.[timeType]?.toUpperCase() },
      { value: 'new', text: 'NEW', width: widthLarge + 30, format: '0', isPercent: false, decimals: 0, isUserType: true, dodDecimals: 1, rainbowKey: 'NEW' },
      { value: 'd2', text: 'Avg.D2', width: widthMid, format: '0.00%', isPercent: true, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Avg.D2' },
      { value: 'd3', text: 'Avg.D3', width: widthMid, format: '0.00%', isPercent: true, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Avg.D3' },
      { value: 'd7', text: 'Avg.D7', width: widthMid, format: '0.00%', isPercent: true, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Avg.D7' },
      { value: 'd15', text: 'Avg.D15', width: widthMid + 20, format: '0.00%', isPercent: true, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Avg.D15' },
      { value: 'd31', text: 'Avg.D31', width: widthMid + 20, format: '0.00%', isPercent: true, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Avg.D31' },
      { value: 'tru', text: 'TNU', width: widthLarge + 30, format: '0', isPercent: false, decimals: 0, isUserType: true, dodDecimals: 1, rainbowKey: 'TNU' },
      { value: 'total_revenue', text: 'Total Revenue($)', width: widthLarge + 30, onlyValueWidth: widthLarge, format: '0.00', isPercent: false, decimals: 2, isUserType: false, dodDecimals: 1, rainbowKey: 'Total Revenue($)' },
    ];
    let result = allColumns;
    if (isCasual) {
      result = result.filter(item => CASUAL_METRICS.includes(item.value));
    }
    if (hiddenMetrics.length > 0) {
      result = result.filter(item => !hiddenMetrics.includes(item.value));
    }
    return result;
  },[timeType, isCasual, isAlignedBy, gameCode]);

  useEffect(() => {
    const dateColumn = {
      key: 'date',
      dateKey: 'time',
      header: localText.date,
      width: switchDatePicker ? 120 : 160,
      fixed: "left",
      align: "left",
      render(record: { date: any; }) {
        const {date} = record;
        if (switchDatePicker || moment(date).toDate() == "Invalid Date") return date;
        const weekDay = getWeekDay(date, weekDayArr)?.slice(1, -1);
        const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
        return <>
          <span className='table-text'>{date}</span>
          {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
        </>;
      }
    };

    const launchDateColumn = {
      key: 'release_time',
      header: isEarliestData ? localText.earliestData : localText.launchDate,
      width: 170,
      fixed: "left",
      align: "left",
      render(record) {
        return (
          <>
            <span className='table-text'>{moment(record.release_time).format("YYYY-MM-DD")}</span>
          </>);
      }
    };

    const gameColum = { key: 'game_name', header: localText.game, width: 200, align: 'left', fixed: 'left' };

    // Accumulate 列
    // 获取维度列定义配置
    const getAccumulateColumns = () => {
      if (accumulates?.length === 1 && accumulates[0] === 'date') return [];
      const columns = [];

      accumulates?.filter(a => a !== 'date').forEach((value) => {
        columns.push({
          key: value,
          header: localText[value],
          width: 200,
          render(record) {
            if (!record?.[value]) return "-";
            const text = getDynamicLocalCnEnText(value, record?.[value], gameCode, localLanguage);
            return (<span className="dbd-table-cell-ellipsis" title={text}> {text} </span>);
          }
        });
      });
      return columns;
    };
    const isShowUpDown = showType !== "value_only";
    const upDownWidth = 75;
    const metricColumns = COLUMNS_CONFIG.reduce((columns: any[], item: any) => {

      if (item.value === "adjusted_revenue" && !appannieTypeSourceList?.includes(source)) return columns;
      let text = localText[item.value];

      item.value === "dau" && (text = localText[nameMap[timeType]]) && switchDatePicker && (text = localText.dau) && (dayMonth.type === "Month") && (text = localText.avgDau);

      columns.push({
        key: item.value+showType,
        copyKey: item.value,
        header: text,
        align: "right",
        width: isShowUpDown ? item.width : item?.onlyValueWidth ? item?.onlyValueWidth : item?.width - upDownWidth,
        explain: isCasual
          ? (localLanguage === 'en-US' ? CASUAL_METRIC_EXPLAIN[item?.rainbowKey]?.en : CASUAL_METRIC_EXPLAIN[item?.rainbowKey]?.cn) || ''
          : competitorReqType !== 'xh' ? getExplainVal(explainInfo, item?.rainbowKey, localLanguage) : '',
        isPercent: item.isPercent,
        render(record) {
          const data = record?.[item?.value] || {};
          const isShowChange = showType !== "value_only";

          return (
            <>
              {metricRender(data.value, data[showType.replace("show_", "")], item, record.type, isShowChange)}
            </>
          );
        }
      });
      return columns;
    }, []);
    const newColumns = switchDatePicker ? [dateColumn, launchDateColumn, gameColum, ...getAccumulateColumns(), ...metricColumns] : [dateColumn, gameColum, ...getAccumulateColumns(), ...metricColumns];

    setColumns(newColumns);

  }, [filterValue, accumulates, showType, localLanguage]);

  const visibleMetrics = useMemo(() => {
    return COLUMNS_CONFIG
      .filter(item => !(item.value === 'adjusted_revenue' && !appannieTypeSourceList?.includes(source)))
      .map(item => item.value);
  }, [COLUMNS_CONFIG, source]);

  const buildDownloadMetricsParams = (accumulated: string[]) => {
    const dimensionMetrics = (accumulated || []).filter(dim => dim !== 'date');
    const metricsMap: Record<string, { en: string; zh: string; type?: string; }> = {};

    const localeIdMap: Record<string, { id: string; type?: string; }> = {};
    localTextConfig().forEach(item => {
      localeIdMap[item.key] = { id: item.id };
    });
    COLUMNS_CONFIG.forEach(item => {
      localeIdMap[item.value] = {
        ...localeIdMap[item.value],
        type: item.isPercent ? 'percent' : (item.isUserType ? 'int' : 'float'),
      };
    });

    const getLocaleText = (id: string, values?: Record<string, string | number>) => {
      const allEn: Record<string, string> = { ...enCommon, ...enLocale };
      const allZh: Record<string, string> = { ...zhCommon, ...zhLocale };
      let en = allEn[id] || '';
      let zh = allZh[id] || '';
      if (values) {
        Object.entries(values).forEach(([k, v]) => {
          en = en.replace(`{${k}}`, String(v));
          zh = zh.replace(`{${k}}`, String(v));
        });
      }
      return { en, zh };
    };

    visibleMetrics.forEach(key => {
      let localeKey = key;
      if (key === 'dau') {
        if (switchDatePicker && dayMonth?.type === 'Month') {
          localeKey = 'avgDau';
        } else if (switchDatePicker) {
          localeKey = 'dau';
        } else if (timeType) {
          localeKey = nameMap[timeType] || 'dau';
        }
      }
      const info = localeIdMap[localeKey] || localeIdMap[key];
      if (info) {
        const configItem = localTextConfig().find(i => i.key === localeKey);
        const { en, zh } = getLocaleText(info.id, configItem?.values);
        const typeInfo = localeIdMap[key]?.type;
        metricsMap[key] = { en, zh, ...(typeInfo ? { type: typeInfo } : {}) };
      }
    });
    dimensionMetrics.forEach(key => {
      const info = localeIdMap[key];
      if (info) {
        const { en, zh } = getLocaleText(info.id);
        metricsMap[key] = { en, zh };
      }
    });

    return {
      metrics: visibleMetrics,
      metrics_map: metricsMap,
      ...(dimensionMetrics.length > 0 ? { dimension_metrics: dimensionMetrics } : {}),
    };
  };

  return [columns, setColumns, buildDownloadMetricsParams] as const;
}
